import * as React from 'react';
import clsx from 'clsx';

/**
 * @typedef {{ src: string, type: string }} VideoSource
 */

/**
 * @typedef {Object} VideoProps
 * @property {VideoSource[] | null | undefined} mobileVideo
 * @property {VideoSource[] | null | undefined} desktopVideo
 * @property {string | undefined} mobilePoster
 * @property {string | undefined} desktopPoster
 * @property {'none' | 'metadata' | 'auto' | undefined} preloadMode
 * @property {boolean | undefined} [deferSrcUntilInteraction]
 * @property {boolean | undefined} [deferSrcUntilPageLoad]
 * @property {boolean | undefined} [deferSrcUntilInView]
 * @property {boolean | undefined} hideDesktopOnMobile
 * @property {boolean | undefined} [paused]
 * @property {(() => void) | undefined} [onReady]
 */

/**
 * Users who ask for less motion or less data get the poster only.
 * @returns {boolean}
 */
function prefersStaticMedia() {
  if (typeof window === 'undefined') return false;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData === true;
  return Boolean(reducedMotion || saveData);
}

/**
 * @param {VideoProps} props
 */
function Video({
  mobileVideo,
  desktopVideo,
  mobilePoster,
  desktopPoster,
  preloadMode = 'metadata',
  deferSrcUntilInteraction = false,
  deferSrcUntilPageLoad = false,
  deferSrcUntilInView = false,
  hideDesktopOnMobile = false,
  paused = false,
  onReady,
}) {
  const mobileRef = React.useRef(/** @type {HTMLVideoElement | null} */ (null));
  const desktopRef = React.useRef(/** @type {HTMLVideoElement | null} */ (null));
  const [mobileCanPlay, setMobileCanPlay] = React.useState(false);
  const [desktopCanPlay, setDesktopCanPlay] = React.useState(false);
  const shouldDeferLoading = deferSrcUntilInteraction
    || deferSrcUntilPageLoad
    || deferSrcUntilInView;
  const [shouldLoadVideo, setShouldLoadVideo] = React.useState(!shouldDeferLoading);
  const [mobileNeedsTapToPlay, setMobileNeedsTapToPlay] = React.useState(false);
  const mobileFallbackStyle = React.useMemo(() => {
    if (!mobilePoster) return { backgroundColor: '#0f1115' };
    return {
      backgroundColor: '#0f1115',
      backgroundImage: `url(${mobilePoster})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    };
  }, [mobilePoster]);

  const desktopFallbackStyle = React.useMemo(() => {
    if (!desktopPoster) return { backgroundColor: '#0f1115' };
    return {
      backgroundColor: '#0f1115',
      backgroundImage: `url(${desktopPoster})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    };
  }, [desktopPoster]);

  React.useEffect(() => {
    if (!deferSrcUntilInteraction || shouldLoadVideo) return undefined;

    const loadVideo = () => setShouldLoadVideo(true);
    const options = { once: true, passive: true };

    window.addEventListener('pointerdown', loadVideo, options);
    window.addEventListener('touchstart', loadVideo, options);
    window.addEventListener('keydown', loadVideo, { once: true });
    window.addEventListener('mouseover', loadVideo, options);

    return () => {
      window.removeEventListener('pointerdown', loadVideo);
      window.removeEventListener('touchstart', loadVideo);
      window.removeEventListener('keydown', loadVideo);
      window.removeEventListener('mouseover', loadVideo);
    };
  }, [deferSrcUntilInteraction, shouldLoadVideo]);

  React.useEffect(() => {
    if (!deferSrcUntilPageLoad || shouldLoadVideo) return undefined;
    if (prefersStaticMedia()) return undefined;

    const loadVideo = () => setShouldLoadVideo(true);

    if (document.readyState === 'complete') {
      loadVideo();
      return undefined;
    }

    window.addEventListener('load', loadVideo, { once: true });
    return () => {
      window.removeEventListener('load', loadVideo);
    };
  }, [deferSrcUntilPageLoad, shouldLoadVideo]);

  React.useEffect(() => {
    if (!deferSrcUntilInView || shouldLoadVideo) return undefined;
    if (prefersStaticMedia()) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (isVisible) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01,
      },
    );

    if (mobileRef.current) observer.observe(mobileRef.current);
    if (desktopRef.current) observer.observe(desktopRef.current);

    return () => {
      observer.disconnect();
    };
  }, [deferSrcUntilInView, shouldLoadVideo]);

  React.useEffect(() => {
    if (!shouldLoadVideo) return undefined;

    /**
     * @param {HTMLVideoElement | null} videoEl
     * @param {(() => void) | undefined} onFail
     */
    const tryPlay = (videoEl, onFail) => {
      if (!videoEl) return;
      const target = videoEl;
      target.defaultMuted = true;
      const playResult = target.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {
          if (typeof onFail === 'function') onFail();
        });
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || paused) return;
      tryPlay(mobileRef.current, () => setMobileNeedsTapToPlay(true));
      tryPlay(desktopRef.current, undefined);
    };

    onVisible();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shouldLoadVideo, paused]);

  React.useEffect(() => {
    if (!shouldLoadVideo || !paused) return;
    mobileRef.current?.pause();
    desktopRef.current?.pause();
  }, [shouldLoadVideo, paused]);

  return (
    <>
      {mobileVideo && (
        <>
          {mobilePoster && (
            <img
              src={mobilePoster}
              alt=""
              width="360"
              height="386"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="w-full h-full min-h-[400px] aspect-[4/5] object-cover md:hidden"
            />
          )}
          <video
            ref={mobileRef}
            width="160"
            height="90"
            autoPlay
            muted
            loop
            playsInline
            controls={mobileNeedsTapToPlay}
            preload={preloadMode}
            onCanPlay={() => {
              setMobileCanPlay(true);
              setMobileNeedsTapToPlay(false);
              onReady?.();
            }}
            onLoadedMetadata={() => {
              if (!mobileRef.current) return;
              const playResult = mobileRef.current.play();
              if (playResult && typeof playResult.catch === 'function') {
                playResult.catch(() => setMobileNeedsTapToPlay(true));
              }
            }}
            onClick={() => {
              if (!mobileNeedsTapToPlay || !mobileRef.current) return;
              const playResult = mobileRef.current.play();
              if (playResult && typeof playResult.then === 'function') {
                playResult.then(() => setMobileNeedsTapToPlay(false)).catch(() => {});
              }
            }}
            className={clsx('absolute inset-0 w-full h-full object-cover md:hidden transition-opacity duration-300', mobileCanPlay ? 'opacity-100' : 'opacity-0')}
            style={mobileFallbackStyle}
          >
            {shouldLoadVideo && mobileVideo.map(({ src, type }) => (
              <source
                key={src}
                src={src}
                type={type}
                media="(max-width: 767px)"
              />
            ))}
          </video>
        </>
      )}
      {desktopVideo && (
        <>
          {desktopPoster && (
            <img
              src={desktopPoster}
              alt=""
              width="1920"
              height="999"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className={clsx('w-full h-full min-h-[400px] aspect-video object-cover', (mobileVideo || hideDesktopOnMobile) && 'hidden md:block')}
            />
          )}
          <video
            ref={desktopRef}
            width="160"
            height="90"
            autoPlay
            muted
            loop
            playsInline
            preload={preloadMode}
            onCanPlay={() => {
              setDesktopCanPlay(true);
              onReady?.();
            }}
            onLoadedMetadata={() => {
              if (!desktopRef.current) return;
              const playResult = desktopRef.current.play();
              if (playResult && typeof playResult.catch === 'function') {
                playResult.catch(() => {});
              }
            }}
            className={clsx('absolute inset-0 w-full h-full object-cover transition-opacity duration-300', desktopCanPlay ? 'opacity-100' : 'opacity-0', (mobileVideo || hideDesktopOnMobile) && 'hidden md:block')}
            style={desktopFallbackStyle}
          >
            {shouldLoadVideo && desktopVideo.map(({ src, type }) => (
              <source
                key={src}
                src={src}
                type={type}
                media={(mobileVideo || hideDesktopOnMobile) ? '(min-width: 768px)' : undefined}
              />
            ))}
          </video>
        </>
      )}
    </>
  );
}

export default Video;
